import {
  type TCatFormDataInput,
  type TDiscount,
  type TRecipeSelection,
  type TSubscriptionData,
  DiscountCodeType,
  type TDiscountCodeType,
  FIXED_PRICE_TRIAL_CODES,
} from "@./suf-store";

import { getInterpolationVariables } from "libs/utils/sign-up/fresh";
import { snakeToPascal } from "@utils/misc";

import { TFunctionOf } from "apps/website/types/i18n";

export type InfoListItem = {
  kind: "info";
  id: string;
  label: string;
};

export type CatListItem = {
  kind: "cat";
  id: string; // the cat id
  label: string; // final text to render (e.g. "Daily portions for Cat 1 (x28)")
  isActive: boolean; // drives style and which trailing icon shows
  canToggle: boolean; // whether the trailing action is allowed (e.g. last active cat => false)
};

export type PricingListItem = InfoListItem | CatListItem;

export const getPricingListItems = (
  cats: Record<string, TCatFormDataInput>,
  t: TFunctionOf<"Forms.Fresh.SignUp.Steps.Pricing.TabPanel">,
  isTrial: boolean,
  subscription?: TSubscriptionData,
) => {
  const activeCatsCount = Object.values(cats).filter((cat) => !cat.softDelete).length;
  const canToggleCats = activeCatsCount > 1;

  const catListItems: CatListItem[] = Object.entries(cats).map(([ id, cat ]) => {
    const interpolationVariables = getInterpolationVariables({ name: cat.name });

    return {
      kind: "cat" as const,
      id,
      label: t("TrialTab.List.dailyPortions", interpolationVariables),
      isActive: !cat.softDelete,
      canToggle: canToggleCats,
    };
  });

  const infoTrialItems: InfoListItem[] = [];

  if (isTrial) {
    if (subscription?.sprinklesUpsell?.quantity && subscription.sprinklesUpsell.quantity > 0) {
      infoTrialItems.push(
        {
          kind: "info",
          id: "freeSprinkles",
          label: t("TrialTab.List.freeSprinkles", { count: subscription?.sprinklesUpsell?.quantity ?? 0 }),
        },
      );
    }
    infoTrialItems.push(
      {
        kind: "info",
        id: "freeDelivery",
        label: t("TrialTab.List.freeDelivery"),
      },
    );
  }

  const infoListItems: InfoListItem[] = isTrial
    ? infoTrialItems
    : [
      {
        kind: "info" as const,
        id: "pauseDelayOrCancel",
        label: t("AfterTrialTab.List.pauseDelayOrCancel"),
      },
      {
        kind: "info",
        id: "onHandSupport",
        label: t("AfterTrialTab.List.onHandSupport"),
      },
    ];

  return [ ...catListItems, ...infoListItems ];
};

export const formatPrice = (price?: number | string, fallback = "£--"): string => {
  if (price === undefined || price === null) { return fallback; }
  const num = typeof price === "string" ? Number(price) : price;
  if (Number.isNaN(num)) { return fallback; }
  return `£${num.toFixed(2)}`;
};

export const formatDiscount = (
  discount: TDiscount | undefined,
): string => {
  if (!discount?.type || !discount?.value) { return ""; }

  const type = discount.type.toUpperCase() as TDiscountCodeType;

  switch (type) {
    case DiscountCodeType.PERCENTAGE:
      return `${discount.value}%`;
    case DiscountCodeType.FIXED_AMOUNT:
      return `£${discount.value}`;
    case DiscountCodeType.ABSOLUTE_TOTAL:
      return `£${discount.value}`;
    default: {

      const _exhaustive: never = type;
      console.warn(`Unknown discount type: ${_exhaustive}`);
      return "";
    }
  }
};

export const isAbsoluteDiscount = (discount: TDiscount | undefined): boolean =>
  discount?.type?.toUpperCase() === DiscountCodeType.ABSOLUTE_TOTAL;

export const isFixedPriceTrialDiscount = (discount: TDiscount | undefined): boolean =>
  FIXED_PRICE_TRIAL_CODES.has(discount?.discountCode ?? "");

export const calculateTotalSavings = (
  originalPrice?: number,
  discountedPrice?: number,
): number | undefined => {
  if (typeof originalPrice !== "number" || typeof discountedPrice !== "number") { return undefined; }
  return originalPrice - discountedPrice;
};

export const formatSavings = (savings: number | undefined, fallback = "--"): string => {
  if (savings === undefined || savings === null || Number.isNaN(savings)) { return fallback; }
  return `-£${savings.toFixed(2)}`;
};

/**
 * Formats a cat's selected recipes as a comma-separated, human-readable list,
 * e.g. "6 x Cluck, 3 x Shredded Cluck, 1 x Mooo".
 */
export const formatRecipeList = (
  selection: TRecipeSelection | undefined,
  translateRecipe: (pascalKey: string) => string,
): string => {
  if (!selection?.recipes?.length) { return ""; }
  return selection.recipes
    .map((recipe) => `${recipe.quantity} x ${translateRecipe(snakeToPascal(recipe.type))}`)
    .join(", ");
};
